import { forwardRef, useState } from "react";
import { Box, Grid2, TextField, Typography, useTheme } from "@mui/material";
import { Check, FilterList } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { FloorAvailability } from "../../../features/FloorManager/typings/floor-inventory.entity";
import { getFloorSelectableObjectCount } from "../functions/getFloorInventoryIdsForSelection.function";
import { floorHasSelectableInventory } from "../functions/floorHasSelectableInventory.function";
import { ButtonStyledText } from "../../Common/ButtonStyledText.partial";

type P = {
  floorInventory: FloorAvailability[];
  currentFloor: FloorAvailability;
  onSelectFloor: (id: number, index: number) => void;
  /** When true, floors with only disabled inventories remain selectable (company/location admin). */
  allowInactiveResourceBooking?: boolean;
};

export const FloorFilters: React.FC<P> = forwardRef<HTMLDivElement, P>((props, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    floorInventory,
    currentFloor,
    onSelectFloor,
    allowInactiveResourceBooking = false
  } = props;

  const [filterName, setFilterName] = useState<string>("");

  const handleSelectFloor = (id: number, index: number) => onSelectFloor(id, index);

  return (
    <Box mt={3} width={"100%"} ref={ref}>
      <Grid2 sx={{ mb: 2 }}>
        <TextField
          data-testid="booking-floor-filter-field"
          fullWidth
          placeholder={t("Filter")}
          slotProps={{ input: { startAdornment: <FilterList sx={{ mr: 1 }} /> } }}
          onChange={event => setFilterName(event.target.value.toLowerCase())}
        />
      </Grid2>
      <Grid2>
        {floorInventory
          .filter(inv => {
            if (!filterName) return true;
            return inv.name.toLowerCase().includes(filterName);
          })
          .map(floor => {
            const { id, name } = floor;
            const floorSelectableCount = getFloorSelectableObjectCount(
              floor,
              allowInactiveResourceBooking
            );
            const isFloorSelectable = floorHasSelectableInventory(
              floor,
              allowInactiveResourceBooking
            );

            return (
              <Grid2 key={id} sx={{ pt: 1, pb: 1 }}>
                <ButtonStyledText
                  theme={theme}
                  data-testid={`floor-filter-single-btn-${name}`}
                  fullWidth
                  sx={{ p: "6px 8px 6px 0px" }}
                  disabled={!isFloorSelectable}
                  usePrimaryColor
                  onClick={() => {
                    // prohibit clicking same floor to avoid false triggering
                    if (currentFloor.id === id) return;

                    const selectedFloor = floorInventory.findIndex(inv => floor.id === inv.id);
                    handleSelectFloor(id, selectedFloor);
                  }}
                >
                  <Grid2
                    container
                    width={"inherit"}
                    sx={{ justifyContent: "space-between", alignItems: "center" }}
                  >
                    <Grid2
                      sx={{
                        display: "flex",
                        textWrap: "pretty",
                        overflowWrap: "anywhere",
                        textAlign: "left"
                      }}
                      size={{ xs: 10 }}
                    >
                      {floor.id === currentFloor.id && <Check />}
                      <Typography
                        sx={[
                          { fontWeight: 600 },
                          floor.id === currentFloor.id ? { pl: 1 } : { pl: 4 }
                        ]}
                      >
                        {name}
                      </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 2 }}>
                      <Typography>{floorSelectableCount}</Typography>
                    </Grid2>
                  </Grid2>
                </ButtonStyledText>
              </Grid2>
            );
          })}
      </Grid2>
    </Box>
  );
});
